/**
 * Frontend helper for the image-upload path of the search toolbar
 * (VOY-474). Reads a `File`, base64-encodes it, POSTs to the existing
 * `/api/search/image` endpoint (which now accepts base64 — see backend
 * PR for VOY-474), and returns the normalized result.
 *
 * The returned shape mirrors what `useSearchState.parseAIQuery` consumes
 * so SmartSearchCard can fold the response into state via `setMany` plus
 * a manual merge — no parser layer required.
 */

import { apiFetch } from '@/lib/api'
import type { Cabin, Passengers, TripType } from '@/lib/search/types'

export interface ImageParseResult {
  /** Natural-language summary the backend generated from the image. */
  query?: string
  /** Origin city (typically empty unless `origin` was passed in). */
  origin?: string
  originName?: string
  /** Destination city (the primary signal from vision). */
  destination?: string
  destinationName?: string
  departureDate?: string
  returnDate?: string
  travelers?: { adults?: number; children?: number; childrenAges?: number[] }
  passengers?: Passengers
  tripType?: TripType
  cabin?: Cabin
  /**
   * Image metadata for `state.imageSearch`. The preview URL itself is
   * generated client-side via `fileToPreviewUrl` in the caller — only
   * the description (from vision analysis) lives on the parser result.
   */
  imageSearch: {
    description?: string
  }
}

/**
 * Read a File and resolve to a `{ data, mediaType }` pair suitable for
 * the backend's base64 endpoint. Strips the `data:image/...;base64,`
 * prefix so the payload is raw base64.
 *
 * If the image is larger than `MAX_IMAGE_DIMENSION` on either axis it's
 * downscaled to fit. Claude's vision API rejects images with either
 * dimension >8000 pixels (modern phone photos like the iPhone 14 Pro's
 * 8064×6048 48MP mode exceed that), and Claude's docs recommend keeping
 * the long edge ≤1568 px for best performance. Downscaling client-side
 * also saves the upload bandwidth — a 1568 px JPEG is a fraction of a
 * raw 4K photo.
 */
const MAX_IMAGE_DIMENSION = 1568

export async function fileToBase64(file: File): Promise<{ data: string; mediaType: string }> {
  if (!file.type.startsWith('image/')) {
    throw new Error(`Unsupported file type: ${file.type || 'unknown'}`)
  }

  // Try to downscale. Failure (jsdom, broken image data, unsupported
  // codec like HEIC, etc.) falls back to the original file — keeps
  // tests working and avoids blocking a real upload on a transient
  // canvas error. The backend's 5MB cap is the ultimate safety net.
  let blobToEncode: Blob = file
  let mediaType: string = file.type
  try {
    const downscaled = await downscaleIfNeeded(file, MAX_IMAGE_DIMENSION)
    blobToEncode = downscaled.blob
    mediaType = downscaled.mimeType
  } catch {
    // fall back to the original file; encoded below
  }

  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'))
    reader.readAsDataURL(blobToEncode)
  })
  const commaIdx = dataUrl.indexOf(',')
  if (commaIdx === -1) throw new Error('Could not extract base64 payload from FileReader result')
  return {
    mediaType,
    data: dataUrl.slice(commaIdx + 1),
  }
}

/**
 * If either dimension of `file` exceeds `maxDim`, render the image to a
 * canvas at the largest dimension that fits and export as JPEG (or PNG
 * to preserve transparency). Returns the original file unchanged when
 * it's already within bounds.
 */
async function downscaleIfNeeded(
  file: File,
  maxDim: number,
): Promise<{ blob: Blob; mimeType: string }> {
  const url = URL.createObjectURL(file)
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image()
      i.onload = () => resolve(i)
      i.onerror = () => reject(new Error('Failed to load image'))
      i.src = url
    })

    if (img.naturalWidth <= maxDim && img.naturalHeight <= maxDim) {
      return { blob: file, mimeType: file.type }
    }

    const scale = maxDim / Math.max(img.naturalWidth, img.naturalHeight)
    const newW = Math.round(img.naturalWidth * scale)
    const newH = Math.round(img.naturalHeight * scale)

    const canvas = document.createElement('canvas')
    canvas.width = newW
    canvas.height = newH
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Canvas 2D context unavailable')
    ctx.drawImage(img, 0, 0, newW, newH)

    // Preserve PNG for transparency; everything else (jpeg / heic / webp
    // / gif first-frame) re-encodes to JPEG at quality 0.92 — best
    // bandwidth-vs-detail tradeoff for vision parsing.
    const outType = file.type === 'image/png' ? 'image/png' : 'image/jpeg'
    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('Canvas toBlob returned null'))),
        outType,
        0.92,
      )
    })
    return { blob, mimeType: outType }
  } finally {
    URL.revokeObjectURL(url)
  }
}

/**
 * Convert a File to a `data:` URL for inline preview thumbnail rendering
 * inside the search card. Caller owns lifecycle.
 */
export async function fileToPreviewUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'))
    reader.readAsDataURL(file)
  })
}

interface ImageParseResponse {
  success: boolean
  query?: {
    origin?: string
    originName?: string
    destination?: string
    destinationName?: string
    departureDate?: string
    returnDate?: string
    travelers?: { adults?: number; children?: number; childrenAges?: number[] }
    tripType?: string
    cabin?: string
    duration?: number
    preferences?: string[]
    imageSearch?: boolean
    imageMetadata?: {
      originalUrl?: string | null
      detectedLandmarks?: string[]
      vibe?: string | null
      confidence?: number
      season?: string | null
    }
    parsedBy?: string
  }
  visionAnalysis?: {
    location?: { city?: string; country?: string; confidence?: number }
    landmarks?: Array<{ name: string; confidence: number }>
    confidence?: number
    cached?: boolean
  }
  duration?: number
  error?: string
  details?: string
}

/**
 * Send a file to the backend vision endpoint and return parsed fields
 * in `useSearchState`-compatible shape. Throws on failure with a
 * user-readable message; callers should surface via toast / inline
 * error and clear the preview.
 */
export async function parseImage(file: File, options?: { origin?: string; originName?: string }): Promise<ImageParseResult> {
  const { data, mediaType } = await fileToBase64(file)
  const res = await apiFetch('/api/search/image', {
    method: 'POST',
    body: JSON.stringify({
      imageBase64: data,
      mimeType: mediaType,
      origin: options?.origin,
      originName: options?.originName,
    }),
  })
  const body = (await res.json()) as ImageParseResponse
  if (!res.ok || !body.success) {
    throw new Error(body.details || body.error || `Image search failed (HTTP ${res.status})`)
  }
  const q = body.query ?? {}
  // Build a short human description from the vision analysis so
  // `state.imageSearch.description` carries something useful for downstream UI.
  const detected = body.visionAnalysis?.location
  const description = detected?.city && detected?.country
    ? `${detected.city}, ${detected.country}`
    : undefined
  return {
    query: q.destinationName ? `Trip to ${q.destinationName}` : undefined,
    origin: q.origin,
    originName: q.originName,
    destination: q.destination,
    destinationName: q.destinationName,
    departureDate: q.departureDate,
    returnDate: q.returnDate,
    travelers: q.travelers,
    tripType: isTripType(q.tripType) ? q.tripType : undefined,
    cabin: isCabin(q.cabin) ? q.cabin : undefined,
    imageSearch: {
      description,
    },
  }
}

function isTripType(v: string | undefined): v is TripType {
  return v === 'one-way' || v === 'round-trip' || v === 'multi-city'
}

function isCabin(v: string | undefined): v is Cabin {
  if (!v) return false
  const normalized = v.toLowerCase().replace(/[\s_]+/g, '-')
  return (
    normalized === 'economy' ||
    normalized === 'premium-economy' ||
    normalized === 'business' ||
    normalized === 'first'
  )
}
